import React from 'react';
import { StorylineState } from "../../../store/storyline/types";
import { connect } from "react-redux";
import { RootState } from "../../../store";
import { updateParameterValue } from "../../../store/storyline/actions";
import { DateTimePicker as BaseDateTimePicker } from "../../../shared/components";
import { Moment, default as moment } from 'moment';
import { DocumentedComponent } from '../../../shared/components/DocumentedComponent';
import _ from 'lodash';

interface Props {
    storyline: StorylineState;
    updateParameterValue: typeof updateParameterValue;
    name: string;
    format?: string;
    minDateTime?: string | Date | Moment;
    maxDateTime?: string | Date | Moment;
    onChange?: (value: Moment) => void
}

function _DateTimePicker(props: Props) {
    const { storyline, updateParameterValue, name, format, minDateTime, maxDateTime, onChange, ...other } = props;
    const [value, setValue] = React.useState(storyline.parameterValues.get(name) || null);
    const valueRef = React.useRef(value);
    const [open, setOpen] = React.useState(false);
    const [originalValue, setOriginalValue] = React.useState(value);

    const handleChange = (newValue: Moment) => {
        setValue(newValue);
        valueRef.current = newValue; // The handleClose event handler will read this value in order to an update + execute in the same render cycle.

        if (!open) {
            updateParameterValue(name, newValue);
            onChange?.(newValue);
        }
    };

    const handleOpen = () => {
        setOpen(true);
        setOriginalValue(value);
    }

    const handleClose = () => {
        setOpen(false);

        if (!_.isEqual(originalValue, valueRef.current)) {
            updateParameterValue(name, valueRef.current);
            onChange?.(valueRef.current);
        }
    };

    React.useEffect(() => {
        if (storyline.parameterValues.has(name)) {
            const newValue = storyline.parameterValues.get(name);
            if (newValue !== value) {
                setValue(newValue);
            }
        }
    }, [storyline.parameterValues]);

    return (
        <BaseDateTimePicker
            {...other}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            minDateTime={minDateTime ? moment(minDateTime) : undefined}
            maxDateTime={maxDateTime ? moment(maxDateTime) : undefined}
            value={value}
            onChange={handleChange}
            inputFormat={format || "YYYY/MM/DD HH:mm"}
        />
    );
}

const DateTimePicker = connect(
    (state: RootState) => ({
        storyline: state.storyline
    }),
    { updateParameterValue: updateParameterValue as any })(_DateTimePicker);

(DateTimePicker as DocumentedComponent).metadata = {
    description: `The DateTimePicker component allows the user to select a date (including time) value for a storyline parameter.  It does this via a multi-step calendar + clock control inside a pop-up.

_Use the \`DatePicker\` component if the \`time\` selection is not required._`,
    isSelfClosing: true,
    attributes: [
        { name: "name", type: "string", description: "The name of the parameter to update." },
        { name: "format", type: "string", description: "The format of the date to display in the input box.  Defaults to 'YYYY/MM/DD HH:mm'." },
        { name: "minDateTime", type: "object", description: "The minimum datetime value that can be selected." },
        { name: "maxDateTime", type: "object", description: "The maximum datetime value that can be selected." },
        { name: "onChange", type: "function", template: "onChange={(value) => {$1}}", description: "The callback function that is invoked when the value changes." },
    ]
};

export default DateTimePicker;